import React, { useState, useEffect, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { InputField } from "../../../Components/formElements/InputField";
import { ComboboxField4 } from "../../../Components/formElements/ComboboxField";
import DataTable from "react-data-table-component";

//Components
import Layout from "../../../Layout/Layout";

//API
import ItemService from "../../../services/supplier_appCommonService";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import SupplierConstants from "../../../common/SupplierConstants";

const AdvancePayment = () => {
  localStorage.setItem("menu", "Finance");
  localStorage.setItem("submenu", "Payment");

  const navigate = useNavigate();

  const [isCardVisible, setCardVisible] = useState(true);
  const [isPaymentFromToVisible, setPaymentFromToVisible] = useState(true);
  const [isAccountsVisible, setAccountsVisible] = useState(true);
  const [isAmountVisible, setAmountVisible] = useState(true);
  const [isWriteOffVisible, setWriteOffVisible] = useState(true);
  const [isTransactionsVisible, setTransactionsVisible] = useState(true);
  const [isItemsVisible, setItemsVisible] = useState(true);

  // ─── FORM DATA (with IDs alongside names, numeric amounts) ────────────
  const [formData, setFormData] = useState({
    paymentType: "",
    postingDate: "",
    // Company (ID + name)
    companyId: "",
    company: "",
    // Party Type
    partyType: "",
    // Party (ID + name)
    partyId: "",
    partyName: "",
    party: "",
    partyBalance: 0,
    // Accounts: IDs alongside display names
    accountPaidFromId: "",
    accountPaidFrom: "",
    accountPaidToId: "",
    accountPaidTo: "",
    accountCurrency1: "INR",
    accountCurrency2: "INR",
    // Amounts stored as Numbers
    amount: 0,
    paidAmount: 0,
    totalAllocatedAmount: 0,
    differenceAmount: 0,
    // Transaction fields
    chequeRefNo: "",
    chequeRefDate: "",
    paymentMode: "",
  });

  const [itemRows, setItemRows] = useState([]);
  const [itemErrors, setItemErrors] = useState([]);

  // ─── DROPDOWN OPTIONS ──────────────────────────────────────────────────
  const [companyOptions, setCompanyOptions] = useState([]);
  const [supplierNameOptions, setSupplierNameOptions] = useState([]);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  // Bank/Cash accounts – filtered by selected company
  const [bankAccountOptions, setBankAccountOptions] = useState([]);
  // Supplier bank accounts – from getUserDetail bankDetailsData
  const [supplierBankOptions, setSupplierBankOptions] = useState([]);

  // Manual entry status tracking
  const [isManualPaidFrom, setIsManualPaidFrom] = useState(false);
  const [isManualPaidTo, setIsManualPaidTo] = useState(false);

  const [loadingCompany, setLoadingCompany] = useState(false);
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

  // ─── OPTIONS ──────────────────────────────────────────────────────────
  const paymentTypeOptions = [
    { id: 1, name: "Receive", value: "Receive" },
    { id: 2, name: "Pay", value: "Pay" },
  ];

  const partyTypeOptions = [
    { id: 1, name: "Customer", value: "Customer" },
    { id: 2, name: "Supplier", value: "Supplier" },
  ];

  const paymentModeOptions = [
    { id: 1, name: "Cash", value: "Cash" },
    { id: 2, name: "Cheque", value: "Cheque" },
    { id: 3, name: "NEFT", value: "NEFT" },
    { id: 4, name: "RTGS", value: "RTGS" },
    { id: 5, name: "UPI", value: "UPI" },
  ];

  // ─── TOGGLE FUNCTIONS ─────────────────────────────────────────────────
  const toggleCardVisibility = () => setCardVisible((v) => !v);
  const togglePaymentFromToVisibility = () =>
    setPaymentFromToVisible((v) => !v);
  const toggleAccountsVisibility = () => setAccountsVisible((v) => !v);
  const toggleAmountVisibility = () => setAmountVisible((v) => !v);
  const toggleWriteOffVisibility = () => setWriteOffVisible((v) => !v);
  const toggleTransactionsVisibility = () =>
    setTransactionsVisible((v) => !v);
  const toggleItemsVisibility = () => setItemsVisible((v) => !v);

  // ─── HELPERS ──────────────────────────────────────────────────────────

  /** Safely coerce a value to a number (returns 0 for empty/invalid) */
  const toNum = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };

  /** Format a number for display (INR locale) */
  const fmtINR = (val) => {
    const n = toNum(val);
    return n.toLocaleString("en-IN", { minimumFractionDigits: 2 });
  };

  // ─── FETCH COMPANY LIST ───────────────────────────────────────────────
  const fetchCompanyList = async () => {
    try {
      setLoadingCompany(true);
      const response = await ItemService.generateCustomerQuery({
        queryName: "Get_CompanyMaster_ActiveList",
      });
      if (response?.status === 200 && response.data) {
        const companies = response.data.map((c) => ({
          id: c.code,
          name: c.name,
          code: c.code,
          organisationName: c.organisationName,
          city: c.city,
        }));
        setCompanyOptions(companies);
      }
    } catch (error) {
      console.error("Error fetching company list:", error);
    } finally {
      setLoadingCompany(false);
    }
  };

  // ─── FETCH BANK ACCOUNTS FILTERED BY COMPANY ─────────────────────────
  const fetchBankAccounts = useCallback(
    async (companyId) => {
      if (!companyId) {
        setBankAccountOptions([]);
        return;
      }
      try {
        setLoadingBankAccounts(true);
        const response = await ItemService.generateCustomerQuery({
          queryName: "Get_CompanyMaster_Details",
          documentNumber: companyId,
          timeZone: "Asia/Calcutta",
        });
        if (response?.status === 200 && response.data?.length > 0) {
          const companyData = response.data[0];
          const bankDetails = companyData.bandkDetails || [];
          const accounts = bankDetails.map((acc, index) => ({
            id: acc.bankAccountNumber || String(index),
            name: `${acc.bankBranchName || "Bank"} - ${acc.bankAccountNumber?.slice(-4) || ""} (${acc.AccountHolderName || ""})`,
            value: `${acc.bankBranchName || "Bank"} - ${acc.bankAccountNumber || ""}`,
            accountNumber: acc.bankAccountNumber,
            accountHolderName: acc.AccountHolderName,
            bankName: acc.bankBranchName,
            currency: acc.bankCurrency || "INR",
            type: "bank",
          }));
          setBankAccountOptions([...accounts, { id: "manual", name: "Manual Entry", value: "Manual Entry" }]);
        } else {
          setBankAccountOptions([{ id: "manual", name: "Manual Entry", value: "Manual Entry" }]);
        }
      } catch (error) {
        console.error("Error fetching bank accounts:", error);
        setBankAccountOptions([{ id: "manual", name: "Manual Entry", value: "Manual Entry" }]);
      } finally {
        setLoadingBankAccounts(false);
      }
    },
    []
  );

  // ─── FETCH SUPPLIER LIST ──────────────────────────────────────────────
  const fetchSupplierList = async () => {
    try {
      setLoadingSupplier(true);
      const response = await ItemService.generateQuery({
        queryName: "getRegisteredSupplierList",
      });
      if (response?.status === 200 && response.data) {
        const suppliers = response.data.map((s) => ({
          id: s.userId,
          name: s.name,
          code: s.code,
          organisationName: s.organisationName,
        }));
        setSupplierNameOptions(suppliers);
      }
    } catch (error) {
      console.error("Error fetching supplier list:", error);
    } finally {
      setLoadingSupplier(false);
    }
  };

  // ─── FETCH CUSTOMER LIST ──────────────────────────────────────────────
  const fetchCustomerList = async () => {
    try {
      setLoadingCustomer(true);
      const response = await ItemService.generateCustomerQuery({
        queryName: "getCustomerListC04",
        module: "CUSTOMER",
        searchTerm: "",
        rowFilter: {},
        timeZone: "Asia/Calcutta",
      });
      if (response?.status === 200 && response.data) {
        const customers = response.data.map((c) => ({
          id: c.code,
          name: c.name,
          code: c.code,
          organisationName: c.desc,
        }));
        setCustomerNameOptions(customers);
      }
    } catch (error) {
      console.error("Error fetching customer list:", error);
    } finally {
      setLoadingCustomer(false);
    }
  };

  // ─── INITIAL LOAD ─────────────────────────────────────────────────────
  useEffect(() => {
    fetchCompanyList();
    fetchSupplierList();
    fetchCustomerList();
  }, []);

  // ─── RECOMPUTE ACCOUNTS WHEN PAYMENT TYPE / COMPANY / PARTY CHANGES ──
  /**
   * Core logic for Issue #1:
   * - Pay:    accountPaidFrom = Company bank account (dropdown)
   *           accountPaidTo   = Party ledger (auto, readonly)
   * - Receive: accountPaidFrom = Party ledger (auto, readonly)
   *            accountPaidTo   = Company bank account (dropdown)
   *
   * All params passed explicitly to avoid stale closures.
   */
  const recomputeAccountFields = useCallback(
    (currentPaymentType, currentCompanyId, currentPartyId, currentPartyType, currentPartyName) => {
      const isPay = currentPaymentType === "Pay";

      // Determine which side is the dropdown (bank/cash) vs party ledger (readonly)
      const dropdownSide = isPay ? "paidFrom" : "paidTo";
      const partySide = isPay ? "paidTo" : "paidFrom";

      // Build party ledger label from party selection
      const partyLedgerLabel =
        currentPartyId && currentPartyType
          ? `${currentPartyType} Ledger - ${currentPartyName}`
          : "";

      setFormData((prev) => {
        const updated = { ...prev };

        // Party-side field (readonly ledger)
        if (partySide === "paidFrom") {
          updated.accountPaidFromId = partyLedgerLabel ? "party-ledger" : "";
          updated.accountPaidFrom = partyLedgerLabel;
        } else {
          updated.accountPaidToId = partyLedgerLabel ? "party-ledger" : "";
          updated.accountPaidTo = partyLedgerLabel;
        }

        // Clear the dropdown-side selection so user re-picks when company/party changes
        if (dropdownSide === "paidFrom") {
          updated.accountPaidFromId = "";
          updated.accountPaidFrom = "";
        } else {
          updated.accountPaidToId = "";
          updated.accountPaidTo = "";
        }

        return updated;
      });
      setIsManualPaidFrom(false);
      setIsManualPaidTo(false);
    },
    []
  );

  // ─── HANDLE COMPANY CHANGE ────────────────────────────────────────────
  const handleCompanyChange = async (selectedCompany) => {
    // Find company by displayed name
    const company = companyOptions.find((c) => c.name === selectedCompany);

    const companyId = company ? company.id : "";

    setFormData((prev) => ({
      ...prev,
      companyId: companyId,
      company: selectedCompany,
      accountPaidToId: "",
      accountPaidTo: "",
      accountPaidFromId: "",
      accountPaidFrom: "",
      accountCurrency1: "INR",
      accountCurrency2: "INR",
      partyId: "",
      partyName: "",
      party: "",
      partyBalance: 0,
    }));

    // Clear party-related fields too
    setItemRows([]);
    setItemErrors([]);
    setSupplierBankOptions([]);

    // Fetch bank accounts filtered by this company
    await fetchBankAccounts(companyId);

    // Recompute account fields based on current payment type & new company
    recomputeAccountFields(
      formData.paymentType,
      companyId,
      "",
      formData.partyType,
      ""
    );
  };

  // ─── HANDLE PAYMENT TYPE CHANGE ───────────────────────────────────────
  const handlePaymentTypeChange = (val) => {
    const newPaymentType = val;

    setFormData((prev) => ({
      ...prev,
      paymentType: newPaymentType,
      accountPaidToId: "",
      accountPaidTo: "",
      accountPaidFromId: "",
      accountPaidFrom: "",
    }));

    // Recompute accounts with new payment type (all args explicit, no stale closures)
    recomputeAccountFields(
      newPaymentType,
      formData.companyId,
      formData.partyId,
      formData.partyType,
      formData.partyName
    );
  };

  // ─── HANDLE PARTY TYPE CHANGE ─────────────────────────────────────────
  const handlePartyTypeChange = (selectedPartyType) => {
    setFormData((prev) => ({
      ...prev,
      partyType: selectedPartyType,
      partyName: "",
      partyId: "",
      party: "",
      partyBalance: 0,
      accountPaidToId: "",
      accountPaidTo: "",
      accountPaidFromId: "",
      accountPaidFrom: "",
    }));
    setSupplierBankOptions([]);

    // Recompute account fields after party type changes (party name is now empty)
    recomputeAccountFields(
      formData.paymentType,
      formData.companyId,
      "",
      selectedPartyType,
      "" // partyName cleared
    );
  };

  // ─── HANDLE PARTY NAME CHANGE ─────────────────────────────────────────
  const handlePartyNameChange = async (selectedPartyName) => {
    setFormData((prev) => ({
      ...prev,
      partyName: selectedPartyName,
      partyId: "",
      party: "",
      partyBalance: 0,
      accountPaidToId: "",
      accountPaidTo: "",
      accountPaidFromId: "",
      accountPaidFrom: "",
    }));
    setSupplierBankOptions([]);

    const selectedItem =
      formData.partyType === "Supplier"
        ? supplierNameOptions.find((s) => s.name === selectedPartyName)
        : customerNameOptions.find((c) => c.name === selectedPartyName);

    if (selectedItem) {
      const partyId = selectedItem.id;
      const orgName =
        selectedItem.organisationName || selectedItem.name || selectedPartyName;

      setFormData((prev) => ({
        ...prev,
        partyId: partyId,
      }));

      if (formData.partyType === "Supplier") {
        try {
          console.log("getUserDetail called with userId:", selectedItem.id);
          const response = await ItemService.getUserDetail({
            userId: selectedItem.id,
          });
          console.log("getUserDetail response:", response);
          if (response?.status === 200 && response.data) {
            const partyValue = `${response.data.organisationName} - ${response.data.city || "N/A"}`;
            const balance = toNum(response.data.balance);

            // Populate supplier bank accounts from bankDetailsData, fallback to bankDetails
            let bankDetailsData = response.data.bankDetailsData || [];
            if (bankDetailsData.length === 0) {
              bankDetailsData = response.data.bankDetails || [];
            }
            const supplierBanks = bankDetailsData.map((bank) => ({
              id: bank.id || bank.bankKey || bank.bankAccountNumber,
              name: bank.bankAccountName
                ? `${bank.bankAccountName} - ${bank.bankKey || bank.bankAccountNumber || ""}`
                : `${bank.bankName || "Bank"} - ${bank.bankAccountNumber?.slice(-4) || ""}`,
              value: bank.bankAccountName
                ? `${bank.bankAccountName} - ${bank.bankKey || bank.bankAccountNumber || ""}`
                : `${bank.bankName || "Bank"} - ${bank.bankAccountNumber || ""}`,
              accountNumber: bank.bankKey || bank.bankAccountNumber,
              accountHolderName: bank.bankAccountName || bank.AccountHolderName,
              bankName: bank.bankBranchName || bank.bankAccountName || bank.bankName,
              currency: bank.bankCurrency || "INR",
              type: "bank",
            }));
            setSupplierBankOptions([...supplierBanks, { id: "manual", name: "Manual Entry", value: "Manual Entry" }]);

            setFormData((prev) => ({
              ...prev,
              party: partyValue,
              partyBalance: balance,
            }));
          } else {
            setSupplierBankOptions([{ id: "manual", name: "Manual Entry", value: "Manual Entry" }]);
          }
        } catch (error) {
          console.error("Error fetching supplier details:", error);
          setFormData((prev) => ({
            ...prev,
            party: orgName,
            partyBalance: 0,
          }));
          setSupplierBankOptions([{ id: "manual", name: "Manual Entry", value: "Manual Entry" }]);
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          party: orgName,
          partyBalance: 0,
        }));
      }

      // Recompute account fields now that party is selected
      recomputeAccountFields(
        formData.paymentType,
        formData.companyId,
        partyId,
        formData.partyType,
        selectedPartyName
      );
    }
  };

  // ─── HANDLE BANK ACCOUNT CHANGE (Dropdown) ────────────────────────────
  const handleBankAccountChange = (selectedAccountName, side) => {
    const isPay = formData.paymentType === "Pay";
    const dropdownAccountSide = isPay ? "paidFrom" : "paidTo";

    if (selectedAccountName === "Manual Entry") {
      if (side === "paidFrom") {
        setIsManualPaidFrom(true);
        setFormData((prev) => ({
          ...prev,
          accountPaidFromId: "manual",
          accountPaidFrom: "",
          accountCurrency1: "INR",
        }));
      } else {
        setIsManualPaidTo(true);
        setFormData((prev) => ({
          ...prev,
          accountPaidToId: "manual",
          accountPaidTo: "",
          accountCurrency2: "INR",
        }));
      }
      return;
    }

    const options =
      side === dropdownAccountSide ? bankAccountOptions : supplierBankOptions;
    const account = options.find((a) => a.name === selectedAccountName || a.value === selectedAccountName);

    if (!account) return;

    // The dropdown side (Company Bank/Cash) — directly set the side passed in
    if (side === "paidFrom") {
      setIsManualPaidFrom(false);
      setFormData((prev) => ({
        ...prev,
        accountPaidFromId: account.id || "",
        accountPaidFrom: selectedAccountName,
        accountCurrency1: account.currency || "INR",
      }));
    } else {
      setIsManualPaidTo(false);
      setFormData((prev) => ({
        ...prev,
        accountPaidToId: account.id || "",
        accountPaidTo: selectedAccountName,
        accountCurrency2: account.currency || "INR",
      }));
    }
  };

  // ─── READ-ONLY PARTY LEDGER LABEL ─────────────────────────────────────
  /** Returns the party ledger display label for the non-dropdown account side */


  // Determine which account field is the dropdown vs readonly
  const isPay = formData.paymentType === "Pay";
  const dropdownAccountSide = isPay ? "paidFrom" : "paidTo"; // dropdown side
  const readonlyAccountSide = isPay ? "paidTo" : "paidFrom"; // party ledger side

  // Helper to get current value of a side
  const currentDropdownValue =
    dropdownAccountSide === "paidFrom"
      ? formData.accountPaidFrom
      : formData.accountPaidTo;
  const currentReadonlyValue =
    readonlyAccountSide === "paidFrom"
      ? formData.accountPaidFrom
      : formData.accountPaidTo;

  // ─── GENERIC INPUT / COMBO HANDLERS ────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Convert known numeric fields to numbers
    const numericFields = ["amount", "paidAmount"];
    const sanitizedValue = numericFields.includes(name)
      ? toNum(value)
      : value;

    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleComboChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ─── ITEM ROW HANDLERS ────────────────────────────────────────────────
  const handleItemChange = (index, field, value) => {
    const updated = [...itemRows];
    updated[index][field] = value;

    const errors = [...itemErrors];
    if (!errors[index]) errors[index] = {};

    const totalAmount = toNum(updated[index].totalAmount);
    const outstanding = toNum(updated[index].outstanding);

    // Cascading validation per row: allocated <= outstanding <= totalAmount
    if (field === "totalAmount" && toNum(updated[index].outstanding) > value) {
      errors[index].outstandingError = "Outstanding cannot exceed Total Amount";
    } else if (field === "totalAmount") {
      errors[index].outstandingError = "";
      // If outstanding was valid, check allocated against it
      if (toNum(updated[index].allocated) > toNum(updated[index].outstanding)) {
        errors[index].allocatedError = "Allocated cannot exceed Outstanding";
      } else {
        errors[index].allocatedError = "";
      }
    }

    if (field === "outstanding" && value > totalAmount) {
      errors[index].outstandingError = "Outstanding cannot exceed Total Amount";
      errors[index].outstanding = value;
    } else if (field === "outstanding") {
      errors[index].outstandingError = "";
      if (toNum(updated[index].allocated) > value) {
        errors[index].allocatedError = "Allocated cannot exceed Outstanding";
      } else {
        errors[index].allocatedError = "";
      }
    }

    if (field === "allocated" && value > outstanding) {
      errors[index].allocatedError = "Allocated cannot exceed Outstanding";
    } else if (field === "allocated") {
      errors[index].allocatedError = "";
    }

    setItemRows(updated);
    setItemErrors(errors);

    // Recompute totals after any item change
    recalculateTotals(updated);
  };

  /** Recalculate totalAllocatedAmount and differenceAmount across all items */
  const recalculateTotals = (rows) => {
    let totalAlloc = 0;
    rows?.forEach((row) => {
      totalAlloc += toNum(row.allocated);
    });

    setFormData((prev) => {
      const paidAmt = toNum(prev.paidAmount);
      const diff = paidAmt - totalAlloc;
      return {
        ...prev,
        totalAllocatedAmount: totalAlloc,
        differenceAmount: diff,
      };
    });
  };

  const handleAddItem = () => {
    const newRow = {
      slNo: itemRows.length + 1,
      type: "",
      name: "",
      totalAmount: 0,
      outstanding: 0,
      allocated: 0,
    };
    setItemRows([...itemRows, newRow]);
    setItemErrors([...itemErrors, {}]);
  };

  const handleDeleteItem = (index) => {
    const updated = itemRows.filter((_, i) => i !== index);
    setItemRows(updated.map((row, i) => ({ ...row, slNo: i + 1 })));
    setItemErrors(itemErrors.filter((_, i) => i !== index));
  };

  const itemColumns = [
    {
      name: "Sl.No",
      selector: (row) => row.slNo,
      width: "60px",
    },
    {
      name: "Type",
      cell: (row, index) => (
        <select
          className="inputBox mb-1 form-select"
          value={row.type}
          onChange={(e) => handleItemChange(index, "type", e.target.value)}
          style={{ minWidth: "100px" }}
        >
          <option value="">Select</option>
          <option value="PO">PO</option>
          <option value="Invoice">Invoice</option>
        </select>
      ),
      ignoreRowClick: true,
      button: true,
      width: "120px",
    },
    {
      name: "Reference Name",
      cell: (row, index) => (
        <InputField
          type="text"
          className="inputBox mb-1"
          placeholder="PO/Invoice No"
          value={row.name}
          onChange={(e) => handleItemChange(index, "name", e.target.value)}
        />
      ),
      ignoreRowClick: true,
      button: true,
      width: "150px",
    },
    {
      name: "Total Amount (INR)",
      cell: (row, index) => (
        <>
          <InputField
            type="number"
            className="inputBox mb-1"
            placeholder="0.00"
            value={row.totalAmount}
            onChange={(e) => handleItemChange(index, "totalAmount", toNum(e.target.value))}
          />
          {itemErrors[index]?.outstandingError && (
            <small className="text-danger">{itemErrors[index].outstandingError}</small>
          )}
        </>
      ),
      ignoreRowClick: true,
      button: true,
      width: "150px",
    },
    {
      name: "Outstanding (INR)",
      cell: (row, index) => (
        <>
          <InputField
            type="number"
            className="inputBox mb-1"
            placeholder="0.00"
            value={row.outstanding}
            onChange={(e) => handleItemChange(index, "outstanding", toNum(e.target.value))}
            style={{
              borderColor: itemErrors[index]?.outstandingError ? "#dc3545" : "",
            }}
          />
          {itemErrors[index]?.outstandingError && (
            <small className="text-danger">{itemErrors[index].outstandingError}</small>
          )}
        </>
      ),
      ignoreRowClick: true,
      button: true,
      width: "150px",
    },
    {
      name: "Allocated (INR)",
      cell: (row, index) => (
        <>
          <InputField
            type="number"
            className="inputBox mb-1"
            placeholder="0.00"
            value={row.allocated}
            onChange={(e) => handleItemChange(index, "allocated", toNum(e.target.value))}
            style={{
              borderColor: itemErrors[index]?.allocatedError ? "#dc3545" : "",
            }}
          />
          {itemErrors[index]?.allocatedError && (
            <small className="text-danger">{itemErrors[index].allocatedError}</small>
          )}
        </>
      ),
      ignoreRowClick: true,
      button: true,
      width: "150px",
    },
    {
      name: "Action",
      cell: (row, index) => (
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={() => handleDeleteItem(index)}
          title="Delete"
        >
          <i className="bi bi-trash"></i>
        </button>
      ),
      width: "80px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // ─── CALCULATE TOTALS ─────────────────────────────────────────────────
  const calculateTotals = () => {
    let totalAlloc = 0;
    itemRows.forEach((row) => {
      totalAlloc += toNum(row.allocated);
    });
    return totalAlloc;
  };

  // ─── VALIDATION ───────────────────────────────────────────────────────
  /**
   * Issue #5: Comprehensive validation rules enforced before save.
   */
  const validateForm = () => {
    const errors = [];

    // ── Required fields ──
    if (!formData.paymentType) errors.push("Payment Type is required");
    if (!formData.postingDate) errors.push("Posting Date is required");
    if (!formData.company) errors.push("Company is required");
    if (!formData.partyType) errors.push("Party Type is required");
    if (!formData.partyName) errors.push("Party Name is required");
    if (!formData.party) errors.push("Party is required");

    // ── Account Paid From validation ──
    if (!formData.accountPaidFrom) {
      if (isManualPaidFrom) {
        errors.push("Account Paid From (Manual Entry) is required");
      } else {
        if (isPay) {
          errors.push("Account Paid From (Company Bank/Cash) is required");
        } else {
          errors.push("Account Paid From (Party Ledger) must be auto-filled after selecting Party");
        }
      }
    }

    // ── Account Paid To validation ──
    if (!formData.accountPaidTo) {
      if (isManualPaidTo) {
        errors.push("Account Paid To (Manual Entry) is required");
      } else {
        if (isPay) {
          errors.push("Account Paid To (Party Ledger) must be auto-filled after selecting Party");
        } else {
          errors.push("Account Paid To (Company Bank/Cash) is required");
        }
      }
    }

    // ── Paid Amount is required ──
    if (!formData.paidAmount && formData.paidAmount !== 0)
      errors.push("Paid Amount is required");

    // ── Issue 5a: paidAmount <= amount ──
    if (formData.amount && toNum(formData.paidAmount) > toNum(formData.amount)) {
      errors.push("Paid Amount cannot exceed Amount");
    }

    // ── Issue 5e: accountPaidFrom !== accountPaidTo ──
    const hasIds = formData.accountPaidFromId && formData.accountPaidToId;
    const bothNonManualIds = formData.accountPaidFromId !== "manual" && formData.accountPaidToId !== "manual";
    if (hasIds && bothNonManualIds && formData.accountPaidFromId === formData.accountPaidToId) {
      errors.push("Account Paid From and Account Paid To must be different accounts");
    }
    // Also check by name as a safety net
    if (
      formData.accountPaidFrom &&
      formData.accountPaidTo &&
      formData.accountPaidFrom.trim().toLowerCase() === formData.accountPaidTo.trim().toLowerCase()
    ) {
      errors.push("Account Paid From and Account Paid To must be different accounts");
    }

    // ── Payment Mode validation (Issue 5e continuation) ──
    if (!formData.paymentMode) errors.push("Payment Mode is required");
    if (formData.paymentMode && formData.paymentMode !== "Cash") {
      if (!formData.chequeRefNo)
        errors.push("Reference/Cheque No is required for " + formData.paymentMode);
      if (!formData.chequeRefDate)
        errors.push("Reference/Cheque Date is required for " + formData.paymentMode);
    }

    // ── Items validation ──
    if (itemRows.length === 0) {
      errors.push("At least one Item is required");
    } else {
      itemRows.forEach((item, index) => {
        if (!item.type) errors.push(`Item ${index + 1}: Type is required`);
        if (!item.name) errors.push(`Item ${index + 1}: Reference Name is required`);
        if (!item.totalAmount && item.totalAmount !== 0)
          errors.push(`Item ${index + 1}: Total Amount is required`);
        if (!item.outstanding && item.outstanding !== 0)
          errors.push(`Item ${index + 1}: Outstanding is required`);
        if (!item.allocated && item.allocated !== 0)
          errors.push(`Item ${index + 1}: Allocated Amount is required`);

        // allocated <= outstanding <= totalAmount (per-item)
        const tAmt = toNum(item.totalAmount);
        const outStd = toNum(item.outstanding);
        const alloc = toNum(item.allocated);

        if (tAmt > 0 && outStd > tAmt)
          errors.push(
            `Item ${index + 1}: Outstanding (${fmtINR(outStd)}) cannot exceed Total Amount (${fmtINR(tAmt)})`
          );
        if (outStd > 0 && alloc > outStd)
          errors.push(
            `Item ${index + 1}: Allocated (${fmtINR(alloc)}) cannot exceed Outstanding (${fmtINR(outStd)})`
          );
      });
    }

    // ── Issue 5b: sum(items.allocated) === totalAllocatedAmount ──
    const sumAllocated = itemRows.reduce(
      (sum, row) => sum + toNum(row.allocated),
      0
    );
    const storedTotalAlloc = toNum(formData.totalAllocatedAmount);
    if (itemRows.length > 0 && Math.abs(sumAllocated - storedTotalAlloc) > 0.01) {
      errors.push(
        `Total Allocated Amount (${fmtINR(storedTotalAlloc)}) does not match sum of item allocations (${fmtINR(sumAllocated)})`
      );
    }

    // ── Item-level UI errors ──
    const hasItemErrors = itemErrors.some(
      (err) => err.outstandingError || err.allocatedError || err.totalAmountError
    );
    if (hasItemErrors) {
      errors.push("Please fix errors in Items table before saving");
    }

    return errors;
  };

  // ─── SAVE ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => toast.error(error));
      return;
    }

    const totalAlloc = calculateTotals();
    const paidAmount = toNum(formData.paidAmount);
    const diff = paidAmount - totalAlloc;

    // ── Build payload with IDs + names, numeric amounts ──
    const payload = {
      data: {
        paymentType: formData.paymentType,
        postingDate: formData.postingDate,
        // Company: both ID and name
        companyId: formData.companyId,
        company: formData.company,
        // Party: both ID and name
        partyType: formData.partyType,
        partyId: formData.partyId,
        partyName: formData.partyName,
        party: formData.party,
        partyBalance: toNum(formData.partyBalance),
        // Account Paid From: IDs + name
        accountPaidFromId: formData.accountPaidFromId,
        accountPaidFrom: formData.accountPaidFrom,
        // Account Paid To: IDs + name
        accountPaidToId: formData.accountPaidToId,
        accountPaidTo: formData.accountPaidTo,
        accountCurrency1: formData.accountCurrency1,
        accountCurrency2: formData.accountCurrency2,
        // Numeric amounts
        amount: toNum(formData.amount),
        paidAmount: toNum(formData.paidAmount),
        totalAllocatedAmount: toNum(totalAlloc),
        differenceAmount: toNum(diff),
        // Transaction
        chequeRefNo: formData.chequeRefNo,
        chequeRefDate: formData.chequeRefDate,
        paymentMode: formData.paymentMode,
        // Items (ensure numeric types in item rows)
        items: itemRows.map((row) => ({
          ...row,
          totalAmount: toNum(row.totalAmount),
          outstanding: toNum(row.outstanding),
          allocated: toNum(row.allocated),
        })),
      },
    };

    try {
      const response = await ItemService.supplierAddAdvancePaymentRequest(payload);
      if (response?.status === 200 || response?.status === 201) {
        toast.success("Advance Payment created successfully!");
        handleCancel();
        navigate(SupplierConstants.SUPPLIER_FINANCE_ADVANCE_PAYMENT_LIST);
      } else {
        toast.error(response?.message || "Failed to create Advance Payment");
      }
    } catch (error) {
      console.error("Error creating Advance Payment:", error);
      toast.error("Failed to create Advance Payment");
    }
  };

  // ─── CANCEL / RESET ───────────────────────────────────────────────────
  const handleCancel = () => {
    setFormData({
      paymentType: "",
      postingDate: "",
      companyId: "",
      company: "",
      partyType: "",
      partyId: "",
      partyName: "",
      party: "",
      partyBalance: 0,
      accountPaidFromId: "",
      accountPaidFrom: "",
      accountPaidToId: "",
      accountPaidTo: "",
      accountCurrency1: "INR",
      accountCurrency2: "INR",
      amount: 0,
      paidAmount: 0,
      totalAllocatedAmount: 0,
      differenceAmount: 0,
      chequeRefNo: "",
      chequeRefDate: "",
      paymentMode: "",
    });
    setItemRows([]);
    setItemErrors([]);
    setBankAccountOptions([]);
    setSupplierBankOptions([]);
    setIsManualPaidFrom(false);
    setIsManualPaidTo(false);
  };

  // ─── RECOMPUTE totals whenever items or paidAmount changes ────────────
  useEffect(() => {
    recalculateTotals(itemRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemRows, formData.paidAmount]);

  // ─── RENDER HELPER ─────────────────────────────────────────────────────
  const renderAccordionHeader = (isVisible, toggle, icon, title, subtitle) => (
    <h2 className="accordion-header">
      <Row className="accordion-button">
        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
          <i
            className={`bi ${isVisible ? "bi-chevron-right" : "bi-chevron-down"}`}
            onClick={toggle}
          ></i>
        </Col>
        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
          <span className="iconLogo">{icon}</span>
        </Col>
        <Col xs={12} md={9} lg={9}>
          <span className="headerTitle">{title}</span>
          {subtitle && <span className="subHeaderTitle">{subtitle}</span>}
        </Col>
      </Row>
    </h2>
  );

  // ─── COMPUTED DISPLAY VALUES ──────────────────────────────────────────
  const totalAlloc = calculateTotals();
  const paidAmount = toNum(formData.paidAmount);
  const diff = paidAmount - totalAlloc;

  const showChequeFields =
    formData.paymentMode && formData.paymentMode !== "Cash";

  // Account paid sides / keys details for rendering
  const isDropdownSideManual = dropdownAccountSide === "paidFrom" ? isManualPaidFrom : isManualPaidTo;
  const isReadonlySideManual = readonlyAccountSide === "paidFrom" ? isManualPaidFrom : isManualPaidTo;
  const dropdownAccountKey = dropdownAccountSide === "paidFrom" ? "accountPaidFrom" : "accountPaidTo";
  const readonlyAccountKey = readonlyAccountSide === "paidFrom" ? "accountPaidFrom" : "accountPaidTo";

  // ─── RENDER ────────────────────────────────────────────────────────────
  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">Advance Payment</h2>
            <p className="subText">Enter Advance Payment Details</p>
            <hr />

            {/* 1. Type of Payment */}
            <div className="accordion mb-4" id="accordionPaymentType">
              <div className="accordion-item">
                {renderAccordionHeader(
                  isCardVisible,
                  toggleCardVisibility,
                  "AP",
                  "1. Type of Payment",
                  "Payment Type, Posting Date and Company"
                )}
                <div
                  className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <ComboboxField4
                          label="a. Payment type"
                          placeholder="Select Payment Type"
                          data={paymentTypeOptions}
                          value={formData.paymentType}
                          onChange={(val) => handlePaymentTypeChange(val)}
                        />
                      </Col>
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="b. Posting Date"
                          type="date"
                          name="postingDate"
                          className="inputBox"
                          value={formData.postingDate}
                          onChange={handleInputChange}
                        />
                      </Col>
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <ComboboxField4
                          label="Company"
                          placeholder={loadingCompany ? "Loading..." : "Select Company"}
                          data={companyOptions}
                          value={formData.company}
                          onChange={handleCompanyChange}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Payment from/To */}
            <div className="accordion mb-4" id="accordionPaymentFromTo">
              <div className="accordion-item">
                {renderAccordionHeader(
                  isPaymentFromToVisible,
                  togglePaymentFromToVisibility,
                  "PF",
                  "2. Payment from/To",
                  "Party Type, Party Name and Party"
                )}
                <div
                  className={`accordion-collapse collapse show ${isPaymentFromToVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <ComboboxField4
                          label="a. Party type"
                          placeholder="Select Party Type"
                          data={partyTypeOptions}
                          value={formData.partyType}
                          onChange={handlePartyTypeChange}
                        />
                      </Col>
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <ComboboxField4
                          label="b. Party Name"
                          placeholder={
                            formData.partyType === "Supplier"
                              ? loadingSupplier
                                ? "Loading..."
                                : "Select Supplier"
                              : loadingCustomer
                              ? "Loading..."
                              : "Select Customer"
                          }
                          data={
                            formData.partyType === "Supplier"
                              ? supplierNameOptions
                              : customerNameOptions
                          }
                          value={formData.partyName}
                          onChange={handlePartyNameChange}
                        />
                      </Col>
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="c. Party"
                          type="text"
                          name="party"
                          className="inputBox"
                          value={formData.party}
                          disabled={true}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* Accounts – Issue #1 & #2 */}
            <div className="accordion mb-4" id="accordionAccounts">
              <div className="accordion-item">
                {renderAccordionHeader(
                  isAccountsVisible,
                  toggleAccountsVisibility,
                  "AC",
                  "Accounts",
                  ""
                )}
                <div
                  className={`accordion-collapse collapse show ${isAccountsVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2">
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="a. Party balance (INR)"
                          type="text"
                          name="partyBalance"
                          className="inputBox"
                          value={
                            formData.partyBalance
                              ? `₹ ${fmtINR(formData.partyBalance)}`
                              : "₹ 0.00"
                          }
                          readOnly
                        />
                      </Col>

                      {/* ── DROPDOWN SIDE (Company Bank/Cash account) ── */}
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <ComboboxField4
                          label={
                            isPay
                              ? "b. Account Paid From (Company Bank/Cash)"
                              : "c. Account Paid To (Company Bank/Cash)"
                          }
                          placeholder={
                            loadingBankAccounts
                              ? "Loading accounts..."
                              : "Select Bank / Cash Account"
                          }
                          data={bankAccountOptions}
                          value={isDropdownSideManual ? "Manual Entry" : currentDropdownValue}
                          onChange={(val) =>
                            handleBankAccountChange(val, dropdownAccountSide)
                          }
                        />
                      </Col>

                      {/* ── Party Ledger (dropdown from bankDetailsData) ── */}
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <ComboboxField4
                          label={
                            isPay
                              ? "b. Account Paid To (Party Ledger)"
                              : "c. Account Paid From (Party Ledger)"
                          }
                          placeholder={
                            formData.partyType === "Supplier" && supplierBankOptions.length === 0
                              ? "Select Party first"
                              : "Select Bank Account"
                          }
                          data={
                            formData.partyName
                              ? (formData.partyType === "Supplier"
                                  ? supplierBankOptions
                                  : [{ id: "manual", name: "Manual Entry", value: "Manual Entry" }])
                              : []
                          }
                          value={isReadonlySideManual ? "Manual Entry" : currentReadonlyValue}
                          onChange={(val) =>
                            handleBankAccountChange(val, readonlyAccountSide)
                          }
                        />
                      </Col>

                      {/* ── Conditional Manual Entry Text Fields ── */}
                      {isDropdownSideManual && (
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <InputField
                            label={
                              isPay
                                ? "b. Account Paid From (Manual Entry)"
                                : "c. Account Paid To (Manual Entry)"
                            }
                            type="text"
                            name={dropdownAccountKey}
                            className="inputBox"
                            placeholder="Enter Account Name/Details"
                            value={currentDropdownValue}
                            onChange={handleInputChange}
                          />
                        </Col>
                      )}

                      {isReadonlySideManual && (
                        <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                          <InputField
                            label={
                              isPay
                                ? "b. Account Paid To (Manual Entry)"
                                : "c. Account Paid From (Manual Entry)"
                            }
                            type="text"
                            name={readonlyAccountKey}
                            className="inputBox"
                            placeholder="Enter Account Name/Details"
                            value={currentReadonlyValue}
                            onChange={handleInputChange}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row className="pb-4">
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="d. Account currency"
                          type="text"
                          name="accountCurrency1"
                          className="inputBox"
                          value={formData.accountCurrency1}
                          readOnly
                        />
                      </Col>
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="e. Account Currency"
                          type="text"
                          name="accountCurrency2"
                          className="inputBox"
                          value={formData.accountCurrency2}
                          readOnly
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* Amount */}
            <div className="accordion mb-4" id="accordionAmount">
              <div className="accordion-item">
                {renderAccordionHeader(
                  isAmountVisible,
                  toggleAmountVisibility,
                  "AM",
                  "Amount",
                  ""
                )}
                <div
                  className={`accordion-collapse collapse show ${isAmountVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="Amount (Request)"
                          type="number"
                          name="amount"
                          className="inputBox"
                          placeholder="Enter Amount"
                          value={formData.amount || ""}
                          onChange={handleInputChange}
                        />
                      </Col>
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="Paid Amount (INR)"
                          type="number"
                          name="paidAmount"
                          className="inputBox"
                          placeholder="Enter Paid Amount"
                          value={formData.paidAmount || ""}
                          onChange={handleInputChange}
                        />
                        {formData.amount &&
                          paidAmount > toNum(formData.amount) && (
                            <small className="text-danger">
                              Paid Amount cannot exceed Amount
                            </small>
                          )}
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="accordion mb-4" id="accordionItems">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${isItemsVisible ? "bi-chevron-right" : "bi-chevron-down"}`}
                        onClick={toggleItemsVisibility}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">IT</span>
                    </Col>
                    <Col xs={12} md={6} lg={6}>
                      <span className="headerTitle">Items</span>
                    </Col>
                    <Col xs={12} md={4} lg={4} className="text-end">
                      <button className="btnTable" onClick={handleAddItem}>
                        <i className="bi bi-plus-circle"></i> Add Item
                      </button>
                    </Col>
                  </Row>
                </h2>
                <div
                  className={`accordion-collapse collapse show ${isItemsVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <div className="table-responsive pt-2 pb-4">
                      <DataTable
                        columns={itemColumns}
                        data={itemRows}
                        noHeader
                        pagination
                        highlightOnHover
                        striped
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Write off */}
            <div className="accordion mb-4" id="accordionWriteOff">
              <div className="accordion-item">
                {renderAccordionHeader(
                  isWriteOffVisible,
                  toggleWriteOffVisibility,
                  "WO",
                  "Write off",
                  ""
                )}
                <div
                  className={`accordion-collapse collapse show ${isWriteOffVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <InputField
                          label="Total Allocated Amount (INR)"
                          type="text"
                          name="totalAllocatedAmount"
                          className="inputBox"
                          value={fmtINR(totalAlloc)}
                          readOnly
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding d-flex align-items-center"
                      >
                        <div className="w-100">
                          <label className="form-label fw-bold">
                            Difference Amount (INR)
                          </label>
                          <div
                            className="inputBox px-3 py-2"
                            style={{
                              background: "#e9ecef",
                              borderRadius: "4px",
                              color: diff === 0 ? "#198754" : "#dc3545",
                              fontWeight: "bold",
                              border: "1px solid #ced4da",
                            }}
                          >
                            {`₹ ${fmtINR(diff)}`}
                            {diff !== 0 && (
                              <small className="d-block">Unallocated amount</small>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions */}
            <div className="accordion mb-4" id="accordionTransactions">
              <div className="accordion-item">
                {renderAccordionHeader(
                  isTransactionsVisible,
                  toggleTransactionsVisibility,
                  "TR",
                  "Transactions",
                  ""
                )}
                <div
                  className={`accordion-collapse collapse show ${isTransactionsVisible ? "visible" : ""}`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                        <ComboboxField4
                          label="Payment Mode"
                          placeholder="Select Payment Mode"
                          data={paymentModeOptions}
                          value={formData.paymentMode}
                          onChange={(val) => handleComboChange("paymentMode", val)}
                        />
                      </Col>
                      {showChequeFields && (
                        <>
                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              label={`${formData.paymentMode} / Reference No`}
                              type="text"
                              name="chequeRefNo"
                              className="inputBox"
                              placeholder={`Enter ${formData.paymentMode} No`}
                              value={formData.chequeRefNo}
                              onChange={handleInputChange}
                            />
                          </Col>
                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              label={`${formData.paymentMode} / Reference Date`}
                              type="date"
                              name="chequeRefDate"
                              className="inputBox"
                              value={formData.chequeRefDate}
                              onChange={handleInputChange}
                            />
                          </Col>
                        </>
                      )}
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="pageFooter mb-4 pb-2">
              <Row className="rowDirection justify-content-end">
                <Col xs={12} md={2} lg={2} className="">
                  <button className="btnTable mb-1" onClick={handleCancel}>
                    <i className="bi bi-x-circle"></i> Cancel
                  </button>
                </Col>
                <Col xs={12} md={2} lg={2} className="">
                  <button className="btnTable mb-1" onClick={handleSave}>
                    <i className="bi bi-save"></i> Save
                  </button>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </main>
  );
};

export default AdvancePayment;
